import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import moment from 'moment';
import GlobalConfig from '../../../../../config';

// manifests

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';

// props
import { IPaginationResourceShort } from '../../../../../props/general';
import { FilterOptionProps } from '../../../../uiframeworks/filters/panel';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, IContextualMenuItem, Link, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import { useStore } from '../../../../../stores/root';
import Text from './../../../../typography/text';
import SelectedFilter from '../../../../uiframeworks/filters/selected';
import Pagination from '../../../../uiframeworks/pagination';
import PermissionsService from '../../../../../services/permissions';
import { IVesselResourceShortProps } from '../../../../../props/vessels/vessel';
import GeneralService from '../../../../../services/general';
import VesselService from '../../../../../services/vessels/vessel';
import { NavLink } from 'react-router-dom';
import { IVesselEngineResourceShortProps } from '../../../../../props/vessels/engine';
import VesselEngineService from '../../../../../services/vessels/engine';
import VesselEngineTypes from '../../../../../manifests/types/vesselEngine';
import VesselEngineForm from '../form';

type VesselEnginesListProps = {
    vesselId: string;
    qs?: string[];
    engines?: IVesselEngineResourceShortProps[];
    columns?: TVesselEngineColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    hideQuickFilter?: boolean;
    hidePagination?: boolean;
    hideFilter?: boolean;
    variant?: 'card' | 'plain';
}

export type TVesselEngineColumn = 'name' | 'type' | 'status' | 'vessel' | 'actions';

const VesselEnginesList: React.FC<VesselEnginesListProps> = (props: VesselEnginesListProps) => {
    const shownColumns: TVesselEngineColumn[] = props.columns || [
        'name',
        'vessel',
        'type',
        'status',
        'actions'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [engines, setEngines] = React.useState<IVesselEngineResourceShortProps[]>(props.engines || []);
    const [activeEngine, setActiveEngine] = React.useState<IVesselEngineResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [selectedFilter, setSelectedFilter] = React.useState<FilterOptionProps[]>([]);
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "name",
            name: "Name",
            fieldName: "name",
            minWidth: 150,
            isMultiline: true,
            onRender: (item: IVesselEngineResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <NavLink to={`/vessels/engines/${item.id}`}><Text size={'small'}>{item.name}</Text></NavLink>
                </Stack>
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 30,
            maxWidth: 30,
            onRender: (item: IVesselEngineResourceShortProps) => {
                const menuItems:IContextualMenuItem[] = [];

                if (PermissionsService.hasPermission(['vessels.update.all'], user.permissions)) {
                    menuItems.push({
                        key: 'update',
                        text: 'Update',
                        iconProps: {iconName: "Edit"},
                        onClick: () => {
                            setActiveEngine(item);
                            setActiveSurface('engine.update');
                        }
                    });
                }

                return <Stack.Item styles={{ root: { padding: '0px' } }}>
                    {
                        menuItems.length > 0 ? <Stack.Item className={"detailsListActionRow"}>
                            <ActionButton className={'detailsListActionButton'} menuProps={{
                                items: menuItems
                            }} />
                        </Stack.Item> : null
                    }
                </Stack.Item >;
            }
        },
        {
            key: "vessel",
            name: "Vessel",
            fieldName: "vessel",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselEngineResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <NavLink to={`/vessels/${item.id}`}><Text size={'small'}>{item.name}</Text></NavLink>
                </Stack>
            }
        },
        {
            key: "type",
            name: "Type",
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: IVesselEngineResourceShortProps) => {
                const type = VesselEngineTypes.find((e) => e.key === item.type);

                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{type?.text}</Text>
                </Stack>
            }
        },
        {
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 100,
            isMultiline: true,
            onRender: (item: IVesselEngineResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{item.status}</Text>
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.engines) {
            _onRetrieveEngines();
        } else {
            setLoaded(true);
        }
    }, [keyword, window.location.search]);

    const getQueryString = () => {
        let qs: string[] = JSON.parse(JSON.stringify(props.qs || []));
        const params = (new URLSearchParams(window.location.search));

        // get existing query string
        const _p = params.get("_p");

        qs.push(`top=${GlobalConfig.defaultTop}`);
        if ((_p || "").trim() !== "") { qs.push(`page=${_p}`) }
        if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

        qs = qs.filter(function (item, pos) {
            return qs.indexOf(item) == pos;
        });

        return qs.join("&");
    }

    const _onRetrieveEngines = async () => {
        try {
            setLoaded(false);
            const qs = getQueryString();
            const results = await VesselEngineService.retrieve(props.vesselId, qs);

            setEngines(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_vessel_engines_list_error',
                text: 'Failed to retrieve vessel engines list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} styles={{ root: { marginRight: 10 } }} />
                }
            });
        }

        if (!props.hideCreateButton && PermissionsService.hasPermission(['vessels.engines.create'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create engine",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create engine"}
                        iconProps={{ iconName: "Add" }}
                        styles={{ root: { marginLeft: 10 } }}
                        onClick={() => setActiveSurface('vessel.engine.create')} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean, status?: string) => {
        setActiveSurface(undefined);
        setActiveEngine(undefined);

        if (refresh) { 
            _onRetrieveEngines();
        }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        {selectedFilter.length > 0 ? <SelectedFilter selected={selectedFilter} onChange={(selected) => {
            setSelectedFilter(selected);
        }} /> : null}
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack className={styles[props.variant || 'card']} tokens={{ childrenGap: 20 }}>
                {!props.hidePagination && pagination && engines.length > 0 ? <Pagination currentPage={pagination.currentPage} totalPage={pagination.lastPage} text={pagination.total + " engines found"} /> : null}
                {
                    !loaded || (loaded && engines.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={engines}
                                columns={columns.filter((col) => shownColumns.indexOf(col.key as TVesselEngineColumn) > -1)}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && engines.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                    <Text size={'small'}>Engines not found</Text>
                </Stack> : null}
            </Stack>
            {!props.hidePagination && pagination && engines.length > 0 ? <Pagination currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
        {activeSurface === 'engine.create' ? <VesselEngineForm vesselId={props.vesselId} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'engine.update' && activeEngine ? <VesselEngineForm vesselId={props.vesselId} engineId={activeEngine.id} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default VesselEnginesList;
